import { useState } from "react";
import { Calendar, Plus, Check } from "lucide-react";
import imgImageClimbEllaRock from "../../assets/ella-rock.webp";
import imgImageDiyalumaFalls from "../../assets/diyaluma-falls.webp";
import imgImageLunchAtCafeChill from "../../assets/cafe-chill.webp";

export function DayPlannerCard() {
	const [activeView, setActiveView] = useState<"timeline" | "calendar">("timeline");

	return (
		<div
			className="w-[480px] rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:scale-105 relative bg-white"
			style={{
				border: "1px solid #e5e7eb",
			}}
		>
			{/* Number badge */}
			<div
				className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md z-10"
				style={{
					background: "linear-gradient(135deg, #C084FC 0%, #A78BFA 100%)",
				}}
			>
				3
			</div>

			{/* Content */}
			<div className="pt-16 px-6 pb-5">
				<h3 className="text-2xl font-bold mb-2 text-gray-900">Plan Your Days</h3>
				<p className="text-gray-500 text-sm leading-relaxed mb-4">
					Schedule activities, transportation, and accommodations for each day. Build the perfect itinerary.
				</p>

				{/* Tab Buttons */}
				<div className="flex gap-2 mb-4">
					<button
						onClick={() => setActiveView("timeline")}
						className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
							activeView === "timeline" ? "bg-[#A78BFA] text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
						}`}
					>
						<Calendar className="w-3.5 h-3.5" />
						Timeline
					</button>
					<button
						onClick={() => setActiveView("calendar")}
						className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
							activeView === "calendar" ? "bg-[#A78BFA] text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
						}`}
					>
						<Calendar className="w-3.5 h-3.5" />
						Calendar
					</button>
				</div>

				{/* Day Planner Content */}
				<div className="w-full bg-gray-50 rounded-2xl p-4 border border-gray-200">
					{activeView === "timeline" ? (
						// Timeline View
						<div className="space-y-2.5">
							{/* Bus Transportation */}
							<div className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm">
								<div className="flex items-center gap-2.5">
									<div className="w-7 h-7 rounded-lg bg-pink-50 border border-pink-200 flex items-center justify-center flex-shrink-0">
										<svg className="w-3.5 h-3.5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											/>
										</svg>
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-xs font-semibold text-gray-900">
											Bus from Sigiriya to Ella
											<span className="ml-1.5 text-[10px] font-semibold text-pink-500">140km</span>
										</p>
										<p className="text-[10px] text-gray-500 mt-0.5">12:00 - 18:00</p>
									</div>
								</div>
							</div>

							{/* Activity Items */}
							{[
								{ title: "Climb Ella Rock", time: "06:00 - 09:00", badge: "Booked", image: imgImageClimbEllaRock },
								{ title: "Lunch at Café Chill", time: "12:00 - 13:30", badge: null, image: imgImageLunchAtCafeChill },
								{ title: "Diyaluma Falls", time: "14:00 - 17:00", badge: null, image: imgImageDiyalumaFalls },
							].map((activity, idx) => (
								<div key={idx} className="bg-white rounded-xl p-3 border border-gray-200 shadow-sm flex items-center gap-2.5">
									<div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden">
										<img src={activity.image} alt={activity.title} className="w-full h-full object-cover" />
									</div>
									<div className="flex-1 min-w-0">
										<p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5">
											<span className="w-2.5 h-2.5 rounded-full bg-yellow-400"></span>
											{activity.title}
										</p>
										<p className="text-[10px] text-gray-500 mt-0.5">{activity.time}</p>
									</div>
									{activity.badge && (
										<span className="text-[10px] font-medium text-gray-600 flex items-center gap-1">
											<Check className="w-3 h-3" />
											{activity.badge}
										</span>
									)}
								</div>
							))}

							{/* Add More Button */}
							<button className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-300 text-xs font-semibold text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors flex items-center justify-center gap-1.5">
								<Plus className="w-3.5 h-3.5" />
								Add items to day 1...
							</button>
						</div>
					) : (
						// Calendar View
						<div>
							<div className="bg-white rounded-xl p-3 border border-gray-200">
								<div className="flex items-center justify-between mb-3">
									<p className="text-[10px] font-bold text-gray-700">14 days</p>
									<div className="flex gap-1">
										<button className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
											<svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
												<path
													fillRule="evenodd"
													d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
													clipRule="evenodd"
												/>
											</svg>
										</button>
										<button className="w-5 h-5 rounded bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
											<svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
												<path
													fillRule="evenodd"
													d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
													clipRule="evenodd"
												/>
											</svg>
										</button>
									</div>
								</div>

								{/* Calendar Days */}
								<div className="grid grid-cols-3 gap-2 mb-3">
									{["Day 8\nNuwara Eliya & Ella", "Day 9\nElla", "Day 10\nElla & Arugam Bay"].map((day, idx) => (
										<div key={idx} className="text-center">
											<p className="text-[9px] font-semibold text-gray-700 mb-1 whitespace-pre-line leading-tight">{day}</p>
											<div className="h-1 rounded-full" style={{ background: idx === 1 ? "#FFC14A" : "#E5E7EB" }}></div>
										</div>
									))}
								</div>

								{/* Time Blocks */}
								<div className="space-y-1.5 text-xs">
									{[
										{ time: "8 AM", event: "Car to Ella\n8:45-9:45" },
										{ time: "9 AM", event: "Ella Rock Trailhead\n9:00-11:00" },
										{ time: "10 AM", event: null },
										{ time: "11 AM", event: null },
										{ time: "12 PM", event: null },
									].map((slot, idx) => (
										<div key={idx} className="flex gap-2">
											<span className="text-gray-500 w-10 flex-shrink-0 text-[10px]">{slot.time}</span>
											{slot.event ? (
												<div className="flex-1 bg-pink-50 border border-pink-200 rounded-lg px-2 py-1">
													<p className="text-[9px] font-medium text-gray-700 whitespace-pre-line leading-tight">{slot.event}</p>
												</div>
											) : (
												<div className="flex-1 border-t border-dashed border-gray-200 self-center"></div>
											)}
										</div>
									))}
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
